import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Modal,
  Table,
  Button,
  Form,
  Input,
  Popconfirm,
  message,
  Space,
  Divider,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import {
  useWorkshopsQuery,
  useCreateWorkshopMutation,
  useUpdateWorkshopMutation,
  useDeleteWorkshopMutation,
} from "../../Services";
import { Workshop } from "../../Types/Product";
import { modalFooter, modalTitle } from "../AppUi";

interface WorkshopModalProps {
  open: boolean;
  onClose: () => void;
  onSuccessSelect?: (workshop: Workshop) => void;
}

export const WorkshopModal: React.FC<WorkshopModalProps> = ({
  open,
  onClose,
  onSuccessSelect,
}) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data: workshops, isLoading } = useWorkshopsQuery({
    enabled: open,
  });

  const createMutation = useCreateWorkshopMutation();
  const updateMutation = useUpdateWorkshopMutation();
  const deleteMutation = useDeleteWorkshopMutation();

  const handleStartEdit = (record: Workshop) => {
    setEditingId(record.id);
    form.setFieldsValue({
      name: record.name,
      code: record.code || "",
      description: record.description || "",
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    form.resetFields();
  };

  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingId) {
        updateMutation.mutate(
          {
            id: editingId,
            name: values.name.trim(),
            code: values.code ? values.code.trim() : "",
            description: values.description ? values.description.trim() : "",
          },
          {
            onSuccess: (res) => {
              if (res?.success) {
                message.success("Workshop updated successfully!");
                handleCancelEdit();
              } else {
                message.error(res?.message || "Failed to update workshop.");
              }
            },
            onError: () => message.error("Something went wrong."),
          },
        );
      } else {
        createMutation.mutate(values, {
          onSuccess: (res) => {
            if (res?.success) {
              message.success("Workshop added successfully!");
              form.resetFields();
              if (onSuccessSelect && res?.data) {
                onSuccessSelect(res.data);
              }
            } else {
              message.error(res?.message || "Failed to add workshop.");
            }
          },
          onError: () => message.error("Something went wrong."),
        });
      }
    } catch {
      message.error("Please enter a workshop name.");
    }
  };

  const handleDelete = async (id: string) => {
    deleteMutation.mutate(id, {
      onSuccess: (res) => {
        if (res?.success) {
          message.success("Workshop deleted successfully!");
          if (editingId === id) {
            handleCancelEdit();
          }
        } else {
          message.error(res?.message || "Failed to delete workshop.");
        }
      },
      onError: () => message.error("Something went wrong."),
    });
  };

  const handleModalClose = () => {
    handleCancelEdit();
    onClose();
  };

  const columns = [
    {
      title: "Workshop Name",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: Workshop) => (
        <span
          className={`font-medium ${
            record.id === editingId
              ? "text-blue-700 font-bold"
              : "text-gray-700"
          }`}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      width: 90,
      render: (text: string) => text || "-",
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      align: "center" as const,
      render: (_: any, record: Workshop) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EyeOutlined className="text-indigo-600" />}
            title="View Workshop & Draft Items"
            size="small"
            onClick={() => {
              navigate(`/system/workshop/${record.id}`);
              handleModalClose();
            }}
          />
          <Button
            type="text"
            icon={
              <EditOutlined
                className={record.id === editingId ? "text-blue-600" : ""}
              />
            }
            size="small"
            onClick={() => handleStartEdit(record)}
          />
          <Popconfirm
            title="Are you sure you want to delete this workshop?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title={modalTitle("Manage Workshops")}
      open={open}
      onCancel={handleModalClose}
      footer={modalFooter({
        actions: [{ label: "Close", onClick: handleModalClose }],
      })}
      width={550}
    >
      <Form form={form} layout="vertical" className="my-3">
        <div className="flex gap-2">
          <Form.Item
            name="name"
            label="Workshop Name"
            rules={[{ required: true, message: "Workshop name is required" }]}
            style={{ flex: 2, marginBottom: 8 }}
          >
            <Input
              placeholder="e.g. Main Workshop, West Branch"
              prefix={<PlusOutlined />}
            />
          </Form.Item>
          <Form.Item
            name="code"
            label="Code (Optional)"
            style={{ flex: 1, marginBottom: 8 }}
          >
            <Input placeholder="e.g. WS-01" />
          </Form.Item>
        </div>
        <div className="flex items-end justify-between gap-2">
          <Form.Item
            name="description"
            label="Description (Optional)"
            style={{ flex: 1, marginBottom: 0 }}
          >
            <Input placeholder="Notes or location details" />
          </Form.Item>
          <div className="flex gap-2">
            {editingId && (
              <Button size="middle" onClick={handleCancelEdit}>
                Cancel
              </Button>
            )}
            <Button
              type="primary"
              size="middle"
              onClick={handleFormSubmit}
              loading={createMutation.isPending || updateMutation.isPending}
              className={
                editingId
                  ? "bg-blue-600 hover:bg-blue-700 font-semibold"
                  : "bg-emerald-600 hover:bg-emerald-700 font-semibold"
              }
            >
              {editingId ? "Update Workshop" : "Add Workshop"}
            </Button>
          </div>
        </div>
      </Form>

      <Divider style={{ margin: "12px 0" }} />

      <Table
        dataSource={workshops}
        columns={columns}
        rowKey="id"
        size="small"
        loading={isLoading}
        pagination={{ pageSize: 5, size: "small", showSizeChanger: false }}
        bordered
      />
    </Modal>
  );
};
